import { useCallback, useEffect, useRef, useState } from "react";

// Windowing manuale per tabelle <table> vere con migliaia di righe (vedi
// OrdiniMapping.jsx e Revisione.jsx, tabella anteprima): con un file da
// 10.000+ righe, renderizzare un <input> per ogni cella di ogni riga porta
// a centinaia di migliaia di nodi DOM — digitare nel filtro per colonna
// blocca il thread principale per decine di secondi (misurato: oltre 45s
// con 150.000 celle). Sotto la soglia di attivazione il comportamento
// resta IDENTICO a prima (tutte le righe renderizzate, zero rischio per il
// caso comune); sopra la soglia si renderizzano solo le righe vicine alla
// parte visibile del contenitore di scroll, con due <tr> "spaziatrici"
// (altezza = righe non renderizzate × altezza riga) per mantenere corretta
// la scrollbar. Non serve una libreria (react-window & co. non gestiscono
// <table> nativamente, richiederebbero <div> a griglia e riscrivere tutto
// lo stile) né toccare le funzioni che scrivono su celle "a distanza"
// (scriviCellaAnteprima/scriviCella, usate da paste multi-cella e "↓
// riempi"): aggiornano già PRIMA lo stato React e SOLO SE il ref DOM esiste
// scrivono anche il value a mano — una riga fuori dalla finestra riceve
// comunque l'aggiornamento nello stato, e lo mostra corretto quando rientra
// in vista (defaultValue letto di nuovo al montaggio).
const OVERSCAN = 15;
const ALTEZZA_RIGA_STIMATA = 31; // px, usata solo prima della prima misura reale

export function useFinestraRighe(containerRef, totaleRighe, sogliaAttivazione = 300) {
  const virtualizzata = totaleRighe > sogliaAttivazione;
  const [altezzaRiga, setAltezzaRiga] = useState(ALTEZZA_RIGA_STIMATA);
  const [range, setRange] = useState(() => ({ primo: 0, ultimo: Math.min(totaleRighe, 50) }));
  const primaRigaRef = useRef(null);
  const rafRef = useRef(null);

  const ricalcola = useCallback(() => {
    const contenitore = containerRef.current;
    if (!contenitore) return;
    const scrollTop = contenitore.scrollTop;
    const clientHeight = contenitore.clientHeight || 600;
    const primoVisibile = Math.floor(scrollTop / altezzaRiga);
    const numeroVisibili = Math.ceil(clientHeight / altezzaRiga);
    setRange({
      primo: Math.max(0, primoVisibile - OVERSCAN),
      ultimo: Math.min(totaleRighe, primoVisibile + numeroVisibili + OVERSCAN),
    });
  }, [containerRef, altezzaRiga, totaleRighe]);

  // Ricalcola quando il totale cambia (es. il filtro per colonna riduce le
  // righe risultanti) o quando l'altezza riga viene misurata per la prima
  // volta — sotto soglia l'hook non fa nulla di tutto questo.
  useEffect(() => {
    if (!virtualizzata) return;
    ricalcola();
  }, [virtualizzata, ricalcola]);

  useEffect(() => {
    if (!virtualizzata) return undefined;
    const contenitore = containerRef.current;
    if (!contenitore) return undefined;
    function onScroll() {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        ricalcola();
      });
    }
    contenitore.addEventListener("scroll", onScroll);
    window.addEventListener("resize", onScroll);
    return () => {
      contenitore.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [virtualizzata, containerRef, ricalcola]);

  // Misura l'altezza reale della prima riga renderizzata (una tantum, si
  // stabilizza da sola: dopo l'aggiornamento la differenza scende sotto la
  // soglia e questo effect non tocca più lo stato).
  useEffect(() => {
    if (!virtualizzata || !primaRigaRef.current) return;
    const h = primaRigaRef.current.getBoundingClientRect().height;
    if (h > 0 && Math.abs(h - altezzaRiga) > 0.5) setAltezzaRiga(h);
  });

  if (!virtualizzata) {
    return { virtualizzata: false, indicePrimo: 0, indiceUltimo: totaleRighe, altezzaSopra: 0, altezzaSotto: 0, refPrimaRiga: primaRigaRef };
  }

  const indicePrimo = Math.min(range.primo, totaleRighe);
  const indiceUltimo = Math.min(range.ultimo, totaleRighe);
  return {
    virtualizzata: true,
    indicePrimo,
    indiceUltimo,
    altezzaSopra: indicePrimo * altezzaRiga,
    altezzaSotto: Math.max(0, (totaleRighe - indiceUltimo) * altezzaRiga),
    refPrimaRiga: primaRigaRef,
  };
}
